import {
  computed, getCurrentInstance, onMounted, onUnmounted, ref,
} from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import managementStoreModule from '../managementStoreModule'

const MODULE = 'app-management'

const emptyStep = () => ({
  localKey: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  note: '',
  odometer: '',
  hourmeter: '',
})

const toNumber = value => {
  if (value === '' || value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const nextInterval = (steps, field) => {
  const last = toNumber(steps[steps.length - 1]?.[field])
  const previous = toNumber(steps[steps.length - 2]?.[field])
  if (last == null) return ''
  if (previous != null && last > previous) return String(last + (last - previous))
  return String(last)
}

const normalizeStep = (step = {}) => ({
  ...step,
  localKey: step.id || step.localKey || emptyStep().localKey,
  name: step.name || '',
  note: step.note || '',
  odometer: step.odometer != null ? String(step.odometer) : '',
  hourmeter: step.hourmeter != null ? String(step.hourmeter) : '',
})

export default function useMaintenancePlanForm() {
  const instance = getCurrentInstance()
  const proxy = instance.proxy || instance
  const planId = router.currentRoute.params.id

  const simpleRules = ref(null)
  const isSaving = ref(false)
  const isLoading = ref(false)
  const stepQuery = ref('')
  const didRegisterModule = ref(false)

  const maintenanceData = ref({
    id: '',
    name: '',
    additionalCode: '',
    note: '',
    preventiveMaintenances: [],
  })

  const isEditing = computed(() => Boolean(maintenanceData.value.id))
  const steps = computed(() => maintenanceData.value.preventiveMaintenances)
  const stepsCount = computed(() => steps.value.length)

  const msgDialog = (msg, icon) => {
    proxy.$swal({
      position: 'top-end',
      icon,
      title: msg,
      showConfirmButton: false,
      timer: 4500,
      customClass: { confirmButton: 'btn btn-primary' },
      buttonsStyling: false,
    })
  }

  const payloadFromForm = () => {
    const data = maintenanceData.value
    return {
      ...data,
      name: data.name || '',
      note: data.note || '',
      additionalCode: data.additionalCode || '',
      preventiveMaintenances: (data.preventiveMaintenances || []).map(step => {
        const { localKey, ...rest } = step
        return {
          ...rest,
          name: step.name || '',
          note: step.note || '',
          odometer: step.odometer != null && step.odometer !== '' ? String(step.odometer) : '',
          hourmeter: step.hourmeter != null && step.hourmeter !== '' ? String(step.hourmeter) : '',
        }
      }),
    }
  }

  const fetchMaintenancePlan = () => {
    if (!planId) return Promise.resolve()
    isLoading.value = true
    return store.dispatch(`${MODULE}/fetchMaintenancePlan`, { id: planId })
      .then(response => {
        const data = response.data || {}
        maintenanceData.value = {
          ...data,
          name: data.name || '',
          note: data.note || '',
          additionalCode: data.additionalCode || '',
          preventiveMaintenances: (data.preventiveMaintenances || []).map(normalizeStep),
        }
      })
      .catch(() => {
        msgDialog(proxy.$t('Error searching maintenance plans'), 'error')
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const addStep = () => {
    maintenanceData.value.preventiveMaintenances.push(emptyStep())
  }

  const duplicateLastStep = () => {
    const current = maintenanceData.value.preventiveMaintenances
    if (!current.length) {
      addStep()
      return
    }
    current.push({
      ...emptyStep(),
      name: '',
      note: '',
      odometer: nextInterval(current, 'odometer'),
      hourmeter: nextInterval(current, 'hourmeter'),
    })
  }

  const removeStepLocally = localKey => {
    const list = maintenanceData.value.preventiveMaintenances
    const index = list.findIndex(step => step.localKey === localKey)
    if (index < 0) return
    list.splice(index, 1)
  }

  const confirmRemoveStep = step => {
    const currentPlanId = maintenanceData.value.id || planId
    const stepId = step.id
    const hasContent = Boolean(step.name || step.note || step.odometer || step.hourmeter)

    if (!hasContent && !stepId) {
      removeStepLocally(step.localKey)
      return
    }

    const warningText = (currentPlanId && stepId)
      ? `${proxy.$t("You can't revert your action")}. ${proxy.$t('It is not necessary to save the plan for the deletion to be recorded.')}`
      : proxy.$t("You can't revert your action")

    proxy.$swal({
      title: proxy.$t('Are you sure?'),
      text: warningText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: proxy.$t('Yes'),
      cancelButtonText: proxy.$t('No'),
      customClass: {
        cancelButton: 'cancelBtn',
        confirmButton: 'mr-1',
      },
    }).then(result => {
      if (!result.value) return

      if (currentPlanId && stepId) {
        isLoading.value = true
        store.dispatch(`${MODULE}/deleteMaintenancePlanStep`, {
          preventiveMaintenancePlanId: currentPlanId,
          preventiveMaintenanceId: stepId,
        })
          .then(() => {
            msgDialog(proxy.$t('Step removed successfully'), 'success')
            removeStepLocally(step.localKey)
          })
          .catch(error => {
            const errorMsg = error?.response?.data?.message || proxy.$t('Error removing step')
            msgDialog(errorMsg, 'error')
          })
          .finally(() => {
            isLoading.value = false
          })
      } else {
        removeStepLocally(step.localKey)
      }
    })
  }

  const savePlan = () => {
    isSaving.value = true
    const action = isEditing.value ? 'updateMaintenancePlan' : 'addMaintenancePlan'
    return store.dispatch(`${MODULE}/${action}`, { maintenanceData: payloadFromForm() })
      .then(response => {
        msgDialog(proxy.$t(isEditing.value ? 'Successful update' : 'Successful registration'), 'success')
        if (isEditing.value) return fetchMaintenancePlan()
        const newId = response.data?.id || response.data
        if (newId) {
          return router.push({ name: 'register-maintenance-plan', params: { id: String(newId) } })
        }
        return router.push({ name: 'list-maintenance-plan' })
      })
      .catch(() => {
        msgDialog(proxy.$t(isEditing.value ? 'FAILURE to update' : 'Failed to save'), 'error')
      })
      .finally(() => {
        isSaving.value = false
      })
  }

  const validationForm = () => {
    if (!simpleRules.value) return
    simpleRules.value.validate().then(success => {
      if (success) savePlan()
    })
  }

  const goBack = () => {
    router.push({ name: 'list-maintenance-plan' })
  }

  if (!store.hasModule(MODULE)) {
    store.registerModule(MODULE, managementStoreModule)
    didRegisterModule.value = true
  }

  onMounted(() => {
    fetchMaintenancePlan()
  })

  onUnmounted(() => {
    if (didRegisterModule.value && store.hasModule(MODULE)) {
      store.unregisterModule(MODULE)
    }
  })

  return {
    simpleRules,
    maintenanceData,
    isEditing,
    isSaving,
    isLoading,
    steps,
    stepsCount,
    stepQuery,
    addStep,
    duplicateLastStep,
    confirmRemoveStep,
    validationForm,
    goBack,
    fetchMaintenancePlan,
  }
}
